import { toast } from "react-toastify";

// Tratamiento común de los errores del back:
// - 401: lo gestiona sessionInterceptor (cierra la sesión), aquí no se avisa.
// - 403: sin permiso.
// - 404: el elemento ya no existe; se avisa y se recarga la lista (onNotFound).
// - 409: conflicto de negocio; el back manda el texto en "detail" y se muestra tal cual.
// - 422: "detail" es un texto (se muestra) o una lista de errores de validación,
//   que se asignan a los campos del formulario por el último elemento de "loc".

type ApiErrorOptions = {
  fallback?: string;
  onNotFound?: () => void;
  setFieldErrors?: (errors: Record<string, string>) => void;
};

const DEFAULT_MESSAGE = "Ha ocurrido un error, inténtelo de nuevo";

export const apiErrorStatus = (error: any): number | undefined => error?.response?.status;

// Texto del error que manda el back ({ detail: "..." } o { detail: [{ msg }] }),
// o el genérico si no hay uno utilizable (sin respuesta, página HTML del proxy...)
export const apiErrorMessage = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const messages = detail.map((d: any) => (typeof d === "string" ? d : d?.msg)).filter(Boolean);
    if (messages.length) return messages.join(". ");
  }
  return fallback;
};

export const handleApiError = (error: any, options: ApiErrorOptions = {}) => {
  const { fallback = DEFAULT_MESSAGE, onNotFound, setFieldErrors } = options;
  const status = apiErrorStatus(error);
  const detail = error?.response?.data?.detail;
  const detailText = typeof detail === "string" && detail.trim() !== "" ? detail : null;

  switch (status) {
    case 401:
      return;
    case 403:
      // toastId: si fallan varias peticiones a la vez solo se ve un aviso
      toast.error("No tienes permiso para realizar esta acción", { toastId: "forbidden" });
      return;
    case 404:
      toast.error(detailText ?? "El elemento ya no existe");
      onNotFound?.();
      return;
    case 422:
      if (Array.isArray(detail)) {
        const fieldErrors: Record<string, string> = {};
        detail.forEach((item: any) => {
          const field = item?.loc?.[item.loc.length - 1];
          if (typeof field === "string" && field !== "body") fieldErrors[field] = item.msg;
        });
        if (setFieldErrors && Object.keys(fieldErrors).length > 0) {
          setFieldErrors(fieldErrors);
          toast.error("Revise los campos marcados");
        } else {
          toast.error(detail[0]?.msg ?? fallback);
        }
        return;
      }
      toast.error(detailText ?? fallback);
      return;
    default:
      toast.error(detailText ?? fallback);
  }
};
